<template>
  <button
    class="date-picker-btn-ui"
    :class="{
      'date-picker-btn-ui--default': variant === 'default',
      'date-picker-btn-ui--disabled': disabled,
    }"
    type="button"
    :disabled="disabled"
  >
    <img :src="calendarIcon" alt="Date" class="date-picker-btn-ui__icon" />
    <span class="date-picker-btn-ui__title">
      <template v-if="isOpenEnded">
        <span>From: {{ formattedFrom || 'Select date(s)' }}</span>
      </template>
      <template v-else-if="variant === 'default' && !isSingleDate && formattedTo">
        <span>{{ formattedFrom }}</span>
        <span class="date-picker-btn-ui__separator">/</span>
        <span>{{ formattedTo }}</span>
      </template>
      <template v-else>
        <span>{{ formattedFrom }}</span>
        <span v-if="!isSingleDate && formattedTo">{{ formattedTo }}</span>
      </template>
    </span>
  </button>
</template>

<script lang="ts" setup>
  // Assets
  import calendarIcon from '~/assets/images/icons/calendar.svg';

  // Utils
  import { DateTimeFormatter } from '~/misc/utils/datetime';

  const props = defineProps({
    selectedDate: {
      type: [Array, String, null] as PropType<[string, string] | [string] | string | null>,
      required: true,
    },
    variant: {
      type: String as PropType<'default' | 'compact'>,
      default: 'default',
    },
    allowOpenEnd: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: 'Select date(s)',
    },
  });

  const formatDate = (dateString: string | undefined, isPlaceholder = false): string => {
    if (!dateString) {
      return isPlaceholder ? props.placeholder : '';
    }

    const datePart: string = DateTimeFormatter.toIsoDate(dateString);
    const [year, month, day]: string[] = datePart.split('-');

    if (!year || !month || !day) return isPlaceholder ? props.placeholder : '';

    const result: string = `${day}-${month}-${year}`;

    return result;
  };

  const formattedFrom: ComputedRef<string> = computed<string>(() => {
    if (typeof props.selectedDate === 'string') {
      return formatDate(props.selectedDate);
    }

    return formatDate(props.selectedDate?.[0], true);
  });

  const formattedTo: ComputedRef<string | null> = computed<string | null>(() => {
    if (typeof props.selectedDate === 'string') {
      return null;
    }

    return formatDate(props.selectedDate?.[1]);
  });

  const isSingleDate: ComputedRef<boolean> = computed<boolean>(() => {
    if (typeof props.selectedDate === 'string') {
      return true;
    }

    return formattedFrom.value === formattedTo.value;
  });

  const isOpenEnded: ComputedRef<boolean> = computed<boolean>(() => {
    if (!props.allowOpenEnd) return false;

    const isSingleDateValue: boolean =
      typeof props.selectedDate === 'string' ||
      (Array.isArray(props.selectedDate) && props.selectedDate.length === 1);

    return isSingleDateValue;
  });
</script>
